import { useEffect, useState } from 'react';
import HomeworkStreak, { HOMEWORK_STREAK_GOAL_DAYS } from './HomeworkStreak';
import ProgramPlan from './ProgramPlan';
import AdminFormSubmissions from './AdminFormSubmissions';
import { sendPasswordRecovery } from '../lib/passwordRecovery';
import { supabase } from '../lib/supabase';
import { listStudents, saveHomeworkStreak, saveLearningPlan, type StudentRecord } from '../lib/students';

interface DashboardProps {
  onBack?: () => void;
  onSignOut: () => Promise<void>;
  profileName: string | null;
  userRole?: 'admin' | 'parent' | undefined;
  userId: string;
}

type Kid = {
  id: string;
  name: string;
  firstName: string;
  age?: number | null;
  remainingSessions?: number | null;
  level?: string | null;
  homeworkStreak: number;
  studyMaterialsUrl: string | null;
  sessionReportUrl: string | null;
  learningPlan: string | null;
};

function toKid(student: StudentRecord): Kid {
  return {
    id: student.id,
    name: [student.first_name, student.last_name].filter(Boolean).join(' '),
    firstName: student.first_name,
    homeworkStreak: student.homework_streak,
    studyMaterialsUrl: student.drive_url,
    sessionReportUrl: student.drive_url_2,
    learningPlan: student.learning_plan,
  };
}

function getTrustedGoogleLink(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (
      url.protocol === 'https:' &&
      (url.hostname === 'drive.google.com' || url.hostname === 'docs.google.com')
    ) {
      return url.toString();
    }
  } catch {
    return null;
  }
  return null;
}

function StudentResourceLink({ url, label, color }: { url: string | null; label: string; color: string }) {
  const trustedUrl = getTrustedGoogleLink(url);
  const className = `flex-1 text-center px-3 py-2 text-xs rounded min-h-[44px] flex items-center justify-center ${
    trustedUrl ? `${color} text-white transition-colors` : 'bg-gray-100 text-gray-400 cursor-not-allowed'
  }`;

  if (!trustedUrl) {
    return <span className={className} aria-disabled="true" title="This link has not been added yet">{label}</span>;
  }

  return (
    <a href={trustedUrl} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
    </a>
  );
}

const Dashboard: React.FC<DashboardProps> = ({ onBack, onSignOut, profileName, userRole, userId }) => {
  const [kids, setKids] = useState<Kid[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [planDraft, setPlanDraft] = useState('');
  const [streakSaving, setStreakSaving] = useState(false);
  const [streakStatus, setStreakStatus] = useState<string | null>(null);
  const [streakError, setStreakError] = useState<string | null>(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [passwordRequestSending, setPasswordRequestSending] = useState(false);
  const [passwordRequestStatus, setPasswordRequestStatus] = useState<string | null>(null);
  const [passwordRequestError, setPasswordRequestError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadStudents = async () => {
      if (!active) return;
      try {
        const students = await listStudents(userId, userRole || 'parent');
        if (!active) return;
        const loadedKids = students.map(toKid);
        setKids(loadedKids);
        const firstStudent = loadedKids[0];
        setSelectedStudentId(firstStudent?.id || '');
        setPlanDraft(firstStudent?.learningPlan || '');
      } catch (error) {
        if (!active) return;
        setStudentsError(`Unable to load student profiles: ${error instanceof Error ? error.message : 'Please try again.'}`);
        setStudentsLoading(false);
        return;
      }
      setStudentsLoading(false);
    };

    void loadStudents();
    return () => {
      active = false;
    };
  }, [userId, userRole]);

  const selectedKid = kids.find((kid) => kid.id === selectedStudentId) || null;
  const planDirty = Boolean(selectedKid && planDraft !== (selectedKid.learningPlan || ''));
  const studentNames = kids.map((kid) => kid.firstName).filter(Boolean);
  const parentWelcomeName = studentNames.length < 2
    ? studentNames[0] || 'Student'
    : `${studentNames.slice(0, -1).join(', ')} and ${studentNames[studentNames.length - 1]}`;

  const selectStudent = (studentId: string) => {
    if (planDirty && !window.confirm('Discard your unsaved Program Plan changes and switch students?')) return;
    const nextStudent = kids.find((kid) => kid.id === studentId);
    setSelectedStudentId(studentId);
    setPlanDraft(nextStudent?.learningPlan || '');
    setPlanStatus(null);
    setPlanError(null);
    setStreakStatus(null);
    setStreakError(null);
  };

  const adjustStreak = async (change: -1 | 1) => {
    if (!selectedKid || streakSaving) return;
    const updatedStreak = Math.min(HOMEWORK_STREAK_GOAL_DAYS, Math.max(0, selectedKid.homeworkStreak + change));
    if (updatedStreak === selectedKid.homeworkStreak) return;

    setStreakSaving(true);
    setStreakStatus(null);
    setStreakError(null);
    try {
      const saved = await saveHomeworkStreak(userRole || 'parent', selectedKid.id, selectedKid.homeworkStreak, updatedStreak);
      setKids((current) => current.map((kid) => kid.id === saved.id ? { ...kid, homeworkStreak: saved.homework_streak } : kid));
      setStreakStatus('Homework streak saved.');
    } catch (error) {
      setStreakError(error instanceof Error ? error.message : 'Unable to save homework streak.');
    } finally {
      setStreakSaving(false);
    }
  };

  const savePlan = async () => {
    if (!selectedKid || !planDirty || planSaving) return;
    setPlanSaving(true);
    setPlanStatus(null);
    setPlanError(null);
    try {
      const saved = await saveLearningPlan(userRole || 'parent', selectedKid.id, selectedKid.learningPlan, planDraft);
      const savedPlan = saved.learning_plan || '';
      setKids((current) => current.map((kid) => kid.id === saved.id ? { ...kid, learningPlan: savedPlan } : kid));
      setPlanDraft(savedPlan);
      setPlanStatus('Program Plan saved.');
    } catch (error) {
      setPlanError(error instanceof Error ? error.message : 'Unable to save Program Plan.');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleSignOut = async () => {
    setSignOutError(null);
    try {
      await onSignOut();
    } catch (error) {
      setSignOutError(error instanceof Error ? error.message : 'Unable to sign out. Please try again.');
    }
  };

  const requestPasswordChange = async () => {
    if (passwordRequestSending) return;
    setPasswordRequestSending(true);
    setPasswordRequestStatus(null);
    setPasswordRequestError(null);
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user?.email) {
        throw new Error('Unable to verify your account email. Please sign in again and retry.');
      }
      await sendPasswordRecovery(data.user.email);
      setPasswordRequestStatus('Password reset instructions have been sent to your registered email address.');
    } catch (error) {
      setPasswordRequestError(error instanceof Error ? error.message : 'Unable to send password reset instructions. Please try again.');
    } finally {
      setPasswordRequestSending(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">{userRole === 'admin' ? 'Admin Dashboard' : 'Parents’ Portal'}</h2>
            <p className="text-sm text-gray-500">{userRole === 'admin' ? 'Student progress and program plans' : 'Sessions, payments and your child’s progress'}</p>
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
            <div className="flex min-w-0 flex-col text-right">
              <span className="text-xs text-gray-500">Welcome back,</span>
              <span className="max-w-[28vw] truncate text-sm font-medium sm:max-w-[24vw] sm:text-base lg:max-w-none">
                {userRole === 'admin' ? profileName || 'Admin' : parentWelcomeName}
              </span>
            </div>
            <button
              type="button"
              onClick={() => void requestPasswordChange()}
              disabled={passwordRequestSending}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
            >
              {passwordRequestSending ? 'Sending…' : 'Change Password'}
            </button>
            {onBack && (
              <button
                onClick={onBack}
                className="px-4 py-2.5 bg-white border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
              >
                &larr; Back
              </button>
            )}
            <button
              onClick={() => void handleSignOut()}
              className="px-4 py-2.5 bg-white border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
            >
              Sign out
            </button>
          </div>
        </div>
        {(passwordRequestStatus || passwordRequestError) && (
          <p role={passwordRequestError ? 'alert' : 'status'} className={`mb-4 text-sm ${passwordRequestError ? 'text-red-700' : 'text-green-700'}`}>
            {passwordRequestError || passwordRequestStatus}
          </p>
        )}
        {signOutError && <p role="alert" className="mb-4 text-sm text-red-700">{signOutError}</p>}

        {userRole === 'admin' ? (
          <>
            <section className="space-y-5 rounded-lg bg-white p-4 shadow md:p-6">
              <div className="max-w-xl">
                <label htmlFor="admin-student-select" className="mb-1 block text-sm font-medium text-gray-800">Select student</label>
                <select
                  id="admin-student-select"
                  value={selectedStudentId}
                  onChange={(event) => selectStudent(event.target.value)}
                  disabled={studentsLoading || kids.length === 0 || planSaving || streakSaving}
                  className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 text-sm text-gray-900"
                >
                  {kids.map((kid) => <option key={kid.id} value={kid.id}>{kid.name}</option>)}
                </select>
              </div>
              {studentsLoading && <p role="status" className="text-sm text-gray-500">Loading students…</p>}
              {studentsError && <p role="alert" className="text-sm text-red-700">{studentsError}</p>}
              {!studentsLoading && !studentsError && kids.length === 0 && <p className="text-sm text-gray-500">No student profiles are available.</p>}
              {selectedKid && (
                <div className="grid gap-6 border-t border-gray-100 pt-5 md:grid-cols-2">
                  <HomeworkStreak
                    value={selectedKid.homeworkStreak}
                    editable
                    saving={streakSaving}
                    status={streakStatus}
                    error={streakError}
                    onAdjust={(change) => void adjustStreak(change)}
                  />
                  <ProgramPlan
                    value={selectedKid.learningPlan || ''}
                    draft={planDraft}
                    editable
                    dirty={planDirty}
                    saving={planSaving}
                    status={planStatus}
                    error={planError}
                    onChange={setPlanDraft}
                    onSave={() => void savePlan()}
                  />
                </div>
              )}
            </section>
            <AdminFormSubmissions />
          </>
        ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 md:p-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">My Kids</h3>
                <a
                  href={`${process.env.PUBLIC_URL}/ETA%202027%20Packages.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[44px] items-center text-sm text-mainBlue hover:underline"
                >
                  2027 Packages PDF
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {studentsLoading && <p role="status" className="text-sm text-gray-500">Loading student profiles…</p>}
                    {studentsError && <p role="alert" className="sm:col-span-2 text-sm text-red-700">{studentsError}</p>}
                    {!studentsLoading && !studentsError && kids.map((kid) => (
                      <div key={kid.id} className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                        {/* Avatar + Name */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-mainBlue/10 flex items-center justify-center text-sm font-semibold text-mainBlue flex-shrink-0">
                            {kid.name.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">{kid.name}</div>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 text-sm">
                          <HomeworkStreak value={kid.homeworkStreak} />
                        </div>

                        <div className="mt-4 border-t border-gray-100 pt-3">
                          <ProgramPlan value={kid.learningPlan || ''} />
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                          <StudentResourceLink url={kid.studyMaterialsUrl} label="Study Materials" color="bg-mainPink hover:bg-mainPink/80" />
                          <StudentResourceLink url={kid.sessionReportUrl} label="Session Report" color="bg-mainBlue hover:bg-mainBlue/80" />
                        </div>
                      </div>
                    ))}

                    {/* Empty state card */}
                    {!studentsLoading && !studentsError && kids.length === 0 && (
                      <div className="sm:col-span-2 bg-white rounded-lg border border-dashed border-gray-200 flex items-center justify-center p-6 text-center text-gray-500">
                        <div className="text-xs text-gray-500">No student profiles are assigned to this account.</div>
                      </div>
                    )}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
